// 📄 pages/Ariba/sections/BadiCatalogToolbox.tsx
// Acceso directo (standalone, sin proyectoId) al Catálogo de BAdIs y
// Objetos ABAP desde el Ariba Toolbox — mismo catálogo global de solo
// lectura que /proyecto/:projectId/badis (ver [[BadiCatalogo.tsx]]), pero
// accesible sin estar dentro de un proyecto puntual, para consulta rápida.
import { useEffect, useRef, useState } from 'react';
import { RefreshCw, DatabaseZap, Info } from 'lucide-react';
import Breadcrumbs from '../../../components/ui/Breadcrumbs.tsx';
import { BadiObjeto, getBadis, seedCatalogoBadis } from '../../../services/catalogoBadisService.ts';
import BadiCatalogGrid from '../../../components/abap/BadiCatalogGrid.tsx';
import { useAuth } from '../../../context/AuthContext.tsx';

interface BadiCatalogToolboxProps {
  setPage?: (page: string) => void;
}

export default function BadiCatalogToolbox({ setPage }: BadiCatalogToolboxProps) {
  const { profile } = useAuth();
  const esAdmin = profile?.role === 'admin';
  const [badis, setBadis] = useState<BadiObjeto[]>([]);
  const [loading, setLoading] = useState(true);
  const [sembrando, setSembrando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Mismo criterio que BadiCatalogo.tsx: un consultor no-admin nunca debe
  // intentar sembrar (fallaría con permission-denied), solo un Admin.
  const seedIntentado = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        let data = await getBadis();
        if (data.length === 0 && esAdmin && !seedIntentado.current) {
          seedIntentado.current = true;
          setSembrando(true);
          try {
            await seedCatalogoBadis();
            data = await getBadis();
          } finally {
            setSembrando(false);
          }
        }
        setBadis(data);
      } catch (err) {
        console.error('Error cargando el catálogo de BAdIs:', err);
        setError('No se pudo cargar el catálogo de BAdIs.');
      } finally {
        setLoading(false);
      }
    })();
  }, [esAdmin]);

  return (
    <main className={setPage ? 'min-h-screen p-4 md:p-8 text-white' : 'text-white'}>
      {setPage && (
        <div className="max-w-6xl mx-auto mb-6">
          <Breadcrumbs currentPage="badis-toolbox" setPage={setPage} />
        </div>
      )}

      <div className="max-w-6xl mx-auto space-y-6">
        <header>
          <h1 className="text-2xl font-light text-white">Catálogo de BAdIs y Objetos ABAP</h1>
          <p className="text-sm text-gray-400 mt-1">BAdIs estándar, implementaciones Z, clases, programas e interfaces de las integraciones SAP Ariba / S/4HANA.</p>
        </header>

        <div className="bg-blue-500/10 border border-blue-500/20 text-blue-300 p-3 rounded-lg text-xs flex items-start gap-2">
          <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Catálogo global de referencia técnica, mantenido desde el panel de Administración. Filtra por Solución o Tipo de Objeto y usa el botón de copiar para llevarte el nombre técnico o método a SE18/SE24/SPROXY.</span>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
        )}

        {loading ? (
          <div className="p-10 text-center text-gray-400 flex flex-col items-center gap-3">
            <RefreshCw className="w-6 h-6 animate-spin text-blue-500" />
            <p className="text-sm">{sembrando ? 'Inicializando catálogo con datos de ejemplo...' : 'Cargando catálogo...'}</p>
          </div>
        ) : badis.length === 0 ? (
          <div className="p-10 text-center text-gray-500 bg-white/5 border border-white/10 rounded-2xl">
            <DatabaseZap className="w-8 h-8 mx-auto mb-2 text-gray-600" />
            <p className="text-sm">Todavía no hay objetos registrados en el catálogo. Un Admin puede cargarlos desde /admin.</p>
          </div>
        ) : (
          <BadiCatalogGrid badis={badis} readOnly />
        )}
      </div>
    </main>
  );
}
